// src/components/TeamSettingsCollaborators.tsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";
import {
  Mail, UserPlus, Shield, Trash2, Copy, Check, KeyRound, Loader2, Settings2, User as UserIcon,
} from "lucide-react";
import i18n from "../i18n";
import { getAppBaseUrl } from "../utils/url";

type Role = "owner" | "coach" | "assistant" | "viewer" | "player";
type Status = "invited" | "active" | "revoked";

type MemberRow = {
  id: string;
  team_id: string;
  status: Status;
  role: Role;
  permissions: Record<string, boolean> | null;
  invited_email: string | null;
  user_id: string | null;
  users: { email: string | null } | null; // join alias
  invite_token?: string | null;

  accepted_at?: string | null;
  player_json_id?: string | null; // peut contenir uuid ou un nombre (stringifié)
  player_number?: number | null;
};

type TeamJsonPlayer = {
  id?: string | null;
  number?: number | string | null;
  firstName?: string | null;  // 👈 camelCase
  lastName?: string | null;   // 👈 camelCase
  full_name?: string | null;  // si jamais tu l'utilises ailleurs
  first_name?: string | null; // anciens schémas (fallback)
  last_name?: string | null;  // anciens schémas (fallback)
  name?: string | null;
  photoUrl?: string | null;
  position?: string | null;
};


type TeamRow = { id: string; players?: TeamJsonPlayer[] | null };

type Props = {
  teamId: string;
  frontendUrl?: string;
  onCountsChange?: (c: { total: number; active: number; invited: number; revoked: number }) => void;
};

/* ========= i18n mini-hook ========= */
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key)),
    []
  );
  return { t, lng };
}

/* ========= Helpers ========= */
function tOrDefault(tfn: (k: string) => string, key: string, fallback: string) {
  const v = tfn(key);
  return v && v !== key ? v : fallback;
}

function normalizeUuid(raw?: string | null): string | null {
  if (!raw) return null;
  let s = String(raw).trim();
  try {
    const parsed = JSON.parse(s);
    if (typeof parsed === "string") s = parsed;
  } catch { }
  const m = s.match(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i);
  return m ? m[0] : null;
}

function normalizeMaybeNumber(raw?: string | null): number | null {
  if (!raw) return null;
  let s = String(raw).trim();
  try {
    const parsed = JSON.parse(s);
    if (typeof parsed === "number") return parsed;
    if (typeof parsed === "string") s = parsed;
  } catch { }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function fmtDate(dt?: string | null) {
  if (!dt) return null;
  try {
    return new Date(dt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch { return null; }
}

function displayName(p: TeamJsonPlayer): string {
  const first =
    p.first_name ?? p.firstName ?? "";   // 👈 on prend camelCase en priorité
  const last =
    p.last_name ?? p.lastName ?? "";
  const full =
    (first || last) ? `${first} ${last}`.trim()
      : p.full_name || p.name || "";
  const num = p.number != null && String(p.number).trim() !== "" ? `#${p.number}` : "";
  return [full, num].filter(Boolean).join(" · ");
}


/* ========= Presets ========= */
const PRESETS = {
  owner: { edit_roster: true, edit_matches: true, edit_trainings: true, edit_plays: true, view_finance: true },
  coach: { edit_roster: true, edit_matches: true, edit_trainings: true, edit_plays: true, view_finance: false },
  assistant: { edit_roster: false, edit_matches: true, edit_trainings: true, edit_plays: true, view_finance: false },
  viewer: { edit_roster: false, edit_matches: false, edit_trainings: false, edit_plays: false, view_finance: false },
  player: { edit_roster: false, edit_matches: false, edit_trainings: false, edit_plays: false, view_finance: false },
} as const;
type PermKey = keyof typeof PRESETS["viewer"];
const ALL_PERMS: { key: PermKey; labelI18n: string }[] = [
  { key: "edit_roster", labelI18n: "teams:perm_edit_roster" },
  { key: "edit_matches", labelI18n: "teams:perm_edit_matches" },
  { key: "edit_trainings", labelI18n: "teams:perm_edit_trainings" },
  { key: "edit_plays", labelI18n: "teams:perm_edit_plays" },
  { key: "view_finance", labelI18n: "teams:perm_view_finance" },
];

export default function TeamSettingsCollaborators({ teamId, frontendUrl, onCountsChange }: Props) {
  const { t } = useI18nSafe();

  const origin = frontendUrl || process.env.REACT_APP_FRONTEND_URL || getAppBaseUrl();

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState({ total: 0, active: 0, invited: 0, revoked: 0 });

  // invite
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("assistant");
  const [invitePerms, setInvitePerms] = useState<Record<PermKey, boolean>>({ ...PRESETS.assistant });
  const [savingInvite, setSavingInvite] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // edit perms
  const [editPermsId, setEditPermsId] = useState<string | null>(null);
  const editingMember = useMemo(() => members.find((m) => m.id === editPermsId) || null, [members, editPermsId]);
  const [editPerms, setEditPerms] = useState<Record<PermKey, boolean>>({ ...PRESETS.viewer });
  const [savingPerms, setSavingPerms] = useState(false);

  // roster JSONB indexé
  const [teamPlayersById, setTeamPlayersById] = useState<Record<string, TeamJsonPlayer>>({});
  const [teamPlayersByNumber, setTeamPlayersByNumber] = useState<Record<string, TeamJsonPlayer>>({});

  /* ========= Counts ========= */
  const computeAndSetCounts = useCallback((rows: MemberRow[]) => {
    const active = rows.filter((r) => r.status === "active").length;
    const invited = rows.filter((r) => r.status === "invited").length;
    const revoked = rows.filter((r) => r.status === "revoked").length;
    const total = rows.length;
    const next = { total, active, invited, revoked };
    setCounts(next);
    onCountsChange?.(next);
  }, [onCountsChange]);

  /* ========= Charger le roster JSONB ========= */
  const fetchTeamRoster = useCallback(async () => {
    const { data, error } = await supabase
      .from("teams")
      .select("id, players")
      .eq("id", teamId)
      .maybeSingle<TeamRow>();
    if (error) {
      console.warn("fetch team JSONB players error:", error.message);
      setTeamPlayersById({});
      setTeamPlayersByNumber({});
      return;
    }
    const list = (data?.players || []) as TeamJsonPlayer[];
    const byId: Record<string, TeamJsonPlayer> = {};
    const byNum: Record<string, TeamJsonPlayer> = {};
    for (const p of list) {
      const uuid = normalizeUuid((p as any)?.id ?? null);
      if (uuid) byId[uuid] = p;
      const num = p?.number != null ? String(p.number) : null;
      if (num && !(num in byNum)) byNum[num] = p;
    }
    setTeamPlayersById(byId);
    setTeamPlayersByNumber(byNum);
  }, [teamId]);

  /* ========= Charger members ========= */
  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from("team_members")
      .select(`
        id, team_id, status, role, permissions, invited_email, user_id, invite_token,
        accepted_at, player_json_id, player_number,
        users:user_id(email)
      `)
      .eq("team_id", teamId)
      .order("status", { ascending: false })
      .order("role", { ascending: true });

    if (error) {
      setError(error.message);
      setMembers([]);
      computeAndSetCounts([]);
      setLoading(false);
      return;
    }

    const rows = (data || []) as unknown as MemberRow[];
    setMembers(rows);
    computeAndSetCounts(rows);
    setLoading(false);
  }, [teamId, computeAndSetCounts]);

  useEffect(() => { fetchTeamRoster(); }, [fetchTeamRoster]);
  useEffect(() => { fetchMembers(); }, [fetchMembers]);
  useEffect(() => { if (inviteOpen) setInvitePerms({ ...(PRESETS[inviteRole] as any) }); }, [inviteRole, inviteOpen]);
  useEffect(() => { if (editingMember) setEditPerms((editingMember.permissions ?? PRESETS[editingMember.role]) as any); }, [editingMember]);

  /* ========= Actions ========= */
  async function handleInvite() {
    setSavingInvite(true); setError(null);
    try {
      const inviteToken = crypto.randomUUID();
      const insert = {
        team_id: teamId,
        invited_email: inviteEmail.trim(),
        status: "invited" as Status,
        role: inviteRole,
        permissions: invitePerms,
        invite_token: inviteToken,
      };
      const { error } = await supabase.from("team_members").insert(insert);
      if (error) throw error;
      setInviteOpen(false);
      setInviteEmail("");
      await fetchMembers();
    } catch (e: any) {
      setError(e.message || "Error");
    } finally {
      setSavingInvite(false);
    }
  }
  async function handleRevoke(memberId: string) {
    const { error } = await supabase.from("team_members").update({ status: "revoked" as Status }).eq("id", memberId);
    if (!error) fetchMembers();
  }
  async function handleRoleChange(memberId: string, role: Role) {
    const { error } = await supabase.from("team_members").update({ role }).eq("id", memberId);
    if (!error) fetchMembers();
  }
  async function handleSavePerms() {
    if (!editingMember) return;
    setSavingPerms(true);
    const { error } = await supabase.from("team_members").update({ permissions: editPerms }).eq("id", editingMember.id);
    if (!error) { setEditPermsId(null); fetchMembers(); }
    setSavingPerms(false);
  }
  function inviteLink(inviteToken?: string | null) {
    if (!inviteToken) return "";
    return `${origin}/accept-invite?token=${encodeURIComponent(inviteToken)}`;
  }
  async function copyInviteLink(row: MemberRow) {
    const link = inviteLink(row.invite_token || null);
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopiedId(row.id);
    setTimeout(() => setCopiedId(null), 1500);
  }

  /* ========= Helpers render ========= */
  const AVATAR_COLORS: Record<Role, string> = {
    owner:     "bg-emerald-500 text-white",
    coach:     "bg-blue-500 text-white",
    assistant: "bg-amber-500 text-white",
    viewer:    "bg-gray-400 text-white",
    player:    "bg-violet-500 text-white",
  };

  const STATUS_STYLES: Record<Status, string> = {
    active:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    invited: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    revoked: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
  };

  /* ========= Render ========= */
  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            {t("teams:collaborators")}
          </h3>
          <div className="flex gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">{counts.active} {t("teams:status_active")}</span>
            {counts.invited > 0 && <span className="text-amber-600 dark:text-amber-400 font-medium">{counts.invited} {t("teams:status_invited")}</span>}
            {counts.revoked > 0 && <span>{counts.revoked} {t("teams:status_revoked")}</span>}
          </div>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm transition-colors shadow-sm"
        >
          <UserPlus className="w-4 h-4" />
          {t("teams:invite")}
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/20 px-3 py-2 rounded-lg">{error}</div>}

      {/* Member list */}
      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t("common:loading")}
        </div>
      ) : members.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
          <UserPlus className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("teams:no_collaborators")}</p>
          <button
            onClick={() => setInviteOpen(true)}
            className="mt-3 text-sm text-orange-500 hover:text-orange-600 font-medium"
          >
            {t("teams:invite")} →
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
          {members.map((m) => {
            const email = m.users?.email || m.invited_email || "—";
            const isInvited = m.status === "invited";
            const isRevoked = m.status === "revoked";
            const initials = email.charAt(0).toUpperCase();

            const pidUuid = normalizeUuid(m.player_json_id);
            let linked: TeamJsonPlayer | null = null;
            if (pidUuid && teamPlayersById[pidUuid]) linked = teamPlayersById[pidUuid];
            if (!linked) {
              const num = m.player_number ?? normalizeMaybeNumber(m.player_json_id);
              if (num != null) linked = teamPlayersByNumber[String(num)] ?? null;
            }

            const numInChip = linked?.number != null ? String(linked.number) : null;
            const shouldShowStandaloneNumber = m.player_number != null && String(m.player_number) !== (numInChip ?? "");

            const effectivePerms = m.permissions ?? PRESETS[m.role] ?? {};
            const activePermCount = ALL_PERMS.filter(p => (effectivePerms as any)[p.key]).length;

            return (
              <div key={m.id} className={`flex items-start gap-3 p-4 ${isRevoked ? "opacity-50" : "hover:bg-gray-50 dark:hover:bg-gray-800/40"} transition-colors`}>

                {/* Avatar */}
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${AVATAR_COLORS[m.role]}`}>
                  {initials}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium truncate max-w-[200px] ${isRevoked ? "line-through text-gray-400" : "text-gray-900 dark:text-gray-100"}`}>
                      {email}
                    </span>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLES[m.status]}`}>
                      {t(`teams:status_${m.status}`)}
                    </span>
                  </div>

                  {/* Role + perms */}
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <select
                      value={m.role}
                      onChange={(e) => handleRoleChange(m.id, e.target.value as Role)}
                      disabled={isRevoked}
                      className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-0.5 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 disabled:cursor-not-allowed"
                    >
                      <option value="owner">{t("teams:role_owner")}</option>
                      <option value="coach">{t("teams:role_coach")}</option>
                      <option value="assistant">{t("teams:role_assistant")}</option>
                      <option value="viewer">{t("teams:role_viewer")}</option>
                      <option value="player">{t("teams:role_player")}</option>
                    </select>
                    {m.role !== "owner" && m.role !== "player" && (
                      <span className="text-[11px] text-gray-400">
                        {activePermCount}/{ALL_PERMS.length} {t("teams:permissions").toLowerCase()}
                      </span>
                    )}
                  </div>

                  {/* Linked player (role=player) */}
                  {m.status === "active" && m.role === "player" && (
                    <div className="mt-1.5 flex flex-wrap gap-1 items-center">
                      {linked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                          <UserIcon className="w-3 h-3" />
                          {displayName(linked)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">{tOrDefault(t, "teams:no_linked_player", "Aucun joueur lié")}</span>
                      )}
                      {shouldShowStandaloneNumber && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          #{m.player_number}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Accepted date */}
                  {m.accepted_at && (
                    <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1">
                      ✓ {tOrDefault(t, "teams:accepted_on", "Accepté le")} {fmtDate(m.accepted_at)}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                  {isInvited && (
                    <button
                      onClick={() => copyInviteLink(m)}
                      title={t("teams:copy_link")}
                      className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
                    >
                      {copiedId === m.id ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  )}
                  {!isRevoked && m.role !== "player" && (
                    <button
                      onClick={() => setEditPermsId(m.id)}
                      title={t("teams:edit_permissions")}
                      className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
                    >
                      <Settings2 className="w-4 h-4" />
                    </button>
                  )}
                  {!isRevoked && (
                    <button
                      onClick={() => handleRevoke(m.id)}
                      title={t("common:revoke")}
                      className="p-1.5 rounded-lg border border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-950/20 text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* --- MODALE INVITE --- */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setInviteOpen(false)} />
          <div className="relative z-10 w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-xl overflow-hidden">

            {/* Header modale */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h4 className="text-base font-semibold flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-orange-500" />
                {t("teams:invite_collaborator")}
              </h4>
              <button
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
                onClick={() => setInviteOpen(false)}
              >✕</button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  <Mail className="w-4 h-4 inline mr-1 opacity-60" />
                  {t("common:email")}
                </label>
                <input
                  type="email"
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  placeholder="email@exemple.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>

              {/* Rôle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {t("common:role")}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(["coach", "assistant", "viewer", "player"] as Role[]).map((r) => (
                    <button
                      key={r}
                      onClick={() => setInviteRole(r)}
                      className={`px-3 py-2 rounded-xl border text-sm font-medium text-left transition-colors ${
                        inviteRole === r
                          ? "border-orange-400 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${AVATAR_COLORS[r].split(" ")[0]}`} />
                      {t(`teams:role_${r}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Permissions */}
              {inviteRole !== "player" && (
                <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-3">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    {t("teams:permissions")}
                  </div>
                  <div className="space-y-1.5">
                    {ALL_PERMS.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!invitePerms[p.key]}
                          onChange={(e) => setInvitePerms((prev) => ({ ...prev, [p.key]: e.target.checked }))}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{t(p.labelI18n)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setInviteOpen(false)}
                className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {t("common:cancel")}
              </button>
              <button
                onClick={handleInvite}
                disabled={savingInvite || !inviteEmail.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {savingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                {t("teams:send_invite")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODALE PERMISSIONS --- */}
      {editingMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditPermsId(null)} />
          <div className="relative z-10 w-full max-w-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-base font-semibold flex items-center gap-2">
                <Shield className="w-4 h-4" />
                {t("teams:edit_permissions_for")} {editingMember.users?.email || editingMember.invited_email}
              </h4>
              <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => setEditPermsId(null)}>✕</button>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-3">
              <div className="grid grid-cols-2 gap-2">
                {ALL_PERMS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!editPerms[p.key]}
                      onChange={(e) => setEditPerms((prev) => ({ ...prev, [p.key]: e.target.checked }))}
                    />
                    <span className="text-sm">{t(p.labelI18n)}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">{t("teams:permissions_override_hint")}</p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setEditPermsId(null)} className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800">
                {t("common:cancel")}
              </button>
              <button
                onClick={handleSavePerms}
                disabled={savingPerms}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 text-white disabled:opacity-60"
              >
                {savingPerms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings2 className="w-4 h-4" />}
                {t("common:save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
